import { css } from "@compiled/react";
import { useIntl, FormattedMessage } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import { UbpStep } from "@/components/usage-based-pricing-calculator/src/context";
import {
    useStep,
    useUsageOutput,
    useAllowancesOutput,
    useAiCreditsExceeded,
    useHasCollectionSelected
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { CapacityCard } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";

import { messages } from "./messages";
import { StatusMessage } from "./status-message";

const sidebarStyles = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    backgroundColor: "rgba(23, 23, 23, 0.03)",
    padding: token("space.300"),
    borderRadius: "12px"
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontSize: "20px",
    fontWeight: 700,
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.200")
});

const statusTextStyles = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    color: wacToken("color.primary.black"),
    lineHeight: "1.5"
});

const cardsContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.150"),
    marginBlockEnd: token("space.250")
});

export const CapacitySidebar = () => {
    const intl = useIntl();
    const step = useStep();
    const usage = useUsageOutput();
    const allowances = useAllowancesOutput();
    const aiCreditsExceeded = useAiCreditsExceeded();
    const hasCollectionSelected = useHasCollectionSelected();

    const isDefault = !hasCollectionSelected || step === UbpStep.collections;
    const isExceeds = aiCreditsExceeded;

    const creditsDescription = aiCreditsExceeded
        ? intl.formatMessage(messages.creditsLabelExceeded)
        : intl.formatMessage(messages.creditsLabelDefault, {
              count: Math.round(usage.aiCredits / 4).toLocaleString("en-US")
          });

    return (
        <div css={sidebarStyles} data-testid="capacity-sidebar">
            <Heading size="small" as="h3">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.sidebarTitle} />
                </span>
            </Heading>

            {isDefault ? (
                <p css={statusTextStyles}>
                    <FormattedMessage {...messages.adjustInputsMessage} />
                </p>
            ) : (
                <StatusMessage exceeds={isExceeds} />
            )}

            <div css={cardsContainerStyles}>
                <CapacityCard
                    label="ROVO CREDITS"
                    value={isDefault ? 0 : usage.aiCredits}
                    maxValue={
                        aiCreditsExceeded ? allowances.aiCredits : undefined
                    }
                    status={
                        aiCreditsExceeded
                            ? "error"
                            : isDefault
                            ? "default"
                            : "success"
                    }
                    description={isDefault ? undefined : creditsDescription}
                    isCollapsible={false}
                />
            </div>
        </div>
    );
};
